import React, { memo, MouseEvent, useContext } from 'react'
import { Box, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { UserContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { Equipment } from 'apps/fleet-planner/pages/marketplace/Equipment'
import { Rate } from 'apps/fleet-planner/pages/marketplace/Rate'
import { AuctionShipment } from 'apps/fleet-planner/types'
import { getUserBids } from 'apps/fleet-planner/utils/bids'
import {
  getAuctionshipmentStops,
  getMarketplaceValues,
} from 'apps/fleet-planner/utils/marketplaceHelpers'
import { Button, TableRow, TableCell } from 'transfix-ui'
import { InsightsTags } from './InsightsTags'
import { MarketplaceTableRowLocation } from './MarketplaceTableRowLocation'

interface IMarketplaceTableRow {
  dataTest: string
  id: number
  shipment: AuctionShipment
  onBookNowClick: (id: number) => void
  onSubmitBidClick: (id: number) => void
  onShowDetailsClick: (id: number) => void
}

export const useMarketplaceTableRowStyles = makeStyles(theme => ({
  row: {
    '&:hover, &:focus': {
      backgroundColor: brandColors.coolGray1,
      cursor: 'pointer',
    },
  },
  button: {
    whiteSpace: 'nowrap',
  },
  buttonsContainer: {
    textAlign: 'right',
    paddingRight: '12px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  targetRateContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  insightsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 5,
  },
}))

export const MarketplaceTableRow = memo<IMarketplaceTableRow>(
  ({ dataTest, id, onBookNowClick, onShowDetailsClick, onSubmitBidClick, shipment }) => {
    const { trackEvent } = useFMSAnalytics()
    const classes = useMarketplaceTableRowStyles()
    const { shipmentInfo, tags, targetRate, totalMiles } = getMarketplaceValues(shipment)
    const { pickups } = shipmentInfo

    const { userData } = useContext(UserContext)
    const { motorCarriers = [] } = userData || {}
    const { bids = [] } = shipment || {}
    const userBids = getUserBids(motorCarriers, bids)

    const bookOnTransfixHandler = (e: MouseEvent) => {
      e.stopPropagation()
      trackEvent('Book Load', 'CLICKED_BOOK_NOW_CTA')
      onBookNowClick(id)
    }

    const submitBidHandler = (e: MouseEvent) => {
      e.stopPropagation()
      trackEvent('Book Load', 'CLICKED_SUBMIT_BID_CTA')
      onSubmitBidClick(id)
    }

    const { stops } = getAuctionshipmentStops(shipment)
    const shipmentClickHandler = () => onShowDetailsClick(id)

    return (
      <TableRow data-test={dataTest} onClick={shipmentClickHandler} className={classes.row}>
        <TableCell>
          <div className={classes.targetRateContainer}>
            <Box paddingLeft={2}>
              <Rate totalMiles={totalMiles} targetRate={targetRate} userBids={userBids} />
            </Box>

            <InsightsTags variant={pickups[0].appointmentType} withLabel tags={tags} />
          </div>
        </TableCell>
        <TableCell>
          <MarketplaceTableRowLocation stops={stops} />
        </TableCell>
        <TableCell>
          <Equipment shipment={shipment} />
        </TableCell>
        <TableCell>
          <Box className={classes.buttonsContainer}>
            <Button
              data-test='book-now-button'
              color='primary'
              onClick={bookOnTransfixHandler}
              className={classes.button}>
              Book Now
            </Button>
            <Button
              data-test='submit-bid-button'
              color='secondary'
              onClick={submitBidHandler}
              className={classes.button}>
              Submit Bid
            </Button>
          </Box>
        </TableCell>
      </TableRow>
    )
  }
)
